import {of, combineLatest, BehaviorSubject} from 'rxjs';
import {filter, map, shareReplay} from 'rxjs/operators';
import {get} from 'svelte/store';
import {ty} from '@/da/tingyun';
import {mc} from '@/core';
import {configApi} from '@/api/config';
import {lang$} from '@/store/lang';
import {wwData$} from './worker-data';
import {originalCurrencies$, currencies$} from './config/currency';
import {currenciesBasic$} from './config/currency-basic';
import {currenciesInfo$} from './config/currency-info';
import {contractCollateralLiquility$, contractCollaterals$, contracts$, leverageMargins$} from './config/contract';
import {spots$} from './config/spot';
import {marginCollaterals$, marginRiskLevels$} from './config/margin-trade';
import {riskLimits$, riskLimitCache$} from './config/risk-limit';
import {indexKlineScale$} from './config/index-products';
import {privateProducts$} from './config/private-products';
import {categories$} from './config/market-category';
import './lending/data/index';

export const browser$ = of(process.browser).pipe(filter(Boolean), shareReplay(1));

const products$ = new BehaviorSubject({
  currencies: [],
  contracts: [],
  spots: [],
  riskLimits: [],
  collaterals: [],
  leverages: [],
  marginRiskLevels: null,
  tradeLevels: {blackSymbols: [], levels: []},
  leverageMargins: [],
});
export const tradeLevels$ = new BehaviorSubject({tradeLevels: {blackSymbols: [], levels: []}});

browser$.subscribe(() => Promise.all([loadPrivateProducts(), loadConfig(), loadCoinBasic(), loadCoinInfoList(get(lang$)), loadCategory(get(lang$)), loadProductScale()]));

combineLatest([originalCurrencies$, currenciesBasic$, currenciesInfo$])
  .pipe(map(convertCurrencies), filter(Boolean))
  .subscribe((o) => currencies$.next(o));

combineLatest([products$, currencies$, privateProducts$, categories$]).subscribe(([products, currencies, privateProducts, categories]) => {
  if (currencies.length === 0) {
    return;
  }
  const {contracts, spots, riskLimits, collaterals, marginRiskLevels, leverages, leverageMargins} = products;
  const {currencyList, contractSymbolList, spotSymbolList} = privateProducts;
  const currenciesData = currencies.filter((item) => !currencyList.includes(item.currency));
  const contractsData = convertContractData(contracts, contractSymbolList, categories);
  const collateralsData = collaterals.map((o) => convertCollateralOperationAuthority(o));
  const collateralTradeCurrencies = collateralsData.filter(({canTrade}) => canTrade).map(({currency}) => currency);
  const spotsData = convertSpotsData(spots, spotSymbolList, currenciesData, categories, collateralTradeCurrencies);
  const riskLimitsData = riskLimits.filter((item) => !contractSymbolList.includes(item.symbol));
  const marginPairsData = findMarginPairs(spotsData);
  const marginCurrenciesData = convertMarginCollaterals(collateralsData, currenciesData);

  const contractCollaterals = convertContractCollateral(collaterals);
  const contractCollateralLiquility = contractCollaterals
    .filter((o) => o.isPerpCollateral)
    .sort((a, b) => a.liquidityInDescendingOrder - b.liquidityInDescendingOrder)
    .map((o) => o.currency);

  wwData$.send({
    type: 'foundation-data',
    currencies: currenciesData,
    contracts: contractsData,
    spots: spotsData,
    riskLimits: JSON.parse(JSON.stringify(riskLimitsData)),
    leverages,
    marginCurrencies: marginCurrenciesData,
    marginPairs: marginPairsData,
    marginRiskLevels,
    leverageMargins,
    contractCollateralsCache: contractCollaterals.reduce((p, c) => p.set(c.currency, c), new Map()),
    contractCollateralLiquility,
  });
  marginRiskLevels && marginRiskLevels$.next(marginRiskLevels);
  contracts$.next(contractsData);
  leverageMargins$.next(leverageMargins);
  spots$.next(spotsData);
  riskLimits$.next(riskLimitsData);
  riskLimitCache$.next(riskLimits.reduce((p, c) => p.set(c.symbol, c.riskLimits), new Map()));
  marginCollaterals$.next(collateralsData);
  contractCollaterals$.next(contractCollaterals);
  contractCollateralLiquility$.next(contractCollateralLiquility);
});

async function loadConfig() {
  const productsLoadEvent = ty.createAutoEvent({key: 'products_load'});
  const {data, error} = await configApi.getProductList();
  if (data) {
    productsLoadEvent.end();
    products$.next(data);
    tradeLevels$.next(data.tradeLevels);
    originalCurrencies$.next(data.currencies);
  }
  if (error) {
    productsLoadEvent.fail();
    console.warn('getProductList error', error);
  }
}

async function loadCoinBasic() {
  const {data, error} = await configApi.getCoinBasicList();
  if (data) {
    currenciesBasic$.next(data);
  }
  if (error) {
    console.warn('load coin basic list error', {error});
  }
}

async function loadCoinInfoList(lang) {
  const {data, error} = await configApi.getCoinInfoList({lang});
  if (data) {
    currenciesInfo$.next(data.rows);
  }
  if (error) {
    console.warn('load coin basic list error', {error});
  }
}

function convertCurrencies([currencies, currenciesBasic, currenciesInfo]) {
  if (currencies.length < 1 || currenciesBasic.length < 1 || currenciesInfo.length < 1) {
    return null;
  }
  return currencies.map((item) => {
    const curBasicCurrency = currenciesBasic.find((o) => o.currency === item.currency) || {assetsPrecision: 8};
    const curCurrencyInfo = currenciesInfo.find((o) => o.currency === item.currency) || {fullName: item.name};
    item.valuePrecision = curBasicCurrency.assetsPrecision || 8;
    item.qtyPrecision = ['USD', 'USDT'].indexOf(item.currency) < 0 ? 8 : 2;
    item.valueFactor = Math.pow(10, item.valueScale);
    item.name = curCurrencyInfo.fullName;
    item.depositOpen = curBasicCurrency.depositOpen;
    item.withdrawOpen = curBasicCurrency.withdrawOpen;
    item.chain = curBasicCurrency.chain;
    return item;
  });
}

async function loadProductScale() {
  const {data, error} = await configApi.getProductsScale();
  if (data) {
    indexKlineScale$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

async function loadPrivateProducts() {
  const {data} = await configApi.getPrivateCoin();
  if (data) {
    privateProducts$.next(data);
  }
}

async function loadCategory() {
  const lang = get(lang$);
  const {data, error} = await configApi.getCategoryList({lang});
  if (data) {
    categories$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

function convertCollateralOperationAuthority(collateral) {
  /**
   * markDelete: true means deleted, false means in the list
   * 资产不存在可借贷不可抵押情况
   * 资产下线后，不可借贷、不可交易、不可转入、可还款、可转出
   */
  const online = !collateral.markDelete;
  return {
    ...collateral,
    canTransferOut: true,
    canRepay: true,
    canTransferIn: online,
    canBorrow: online,
    canTrade: online,
  };
}

function findMarginPairs(spots) {
  return spots.reduce((pre, spot) => {
    if (spot.supportMarginTrade) {
      const marginPair = {...spot, mode: 'Margin'};
      pre.push(marginPair);
    }
    return pre;
  }, []);
}

function convertMarginCollaterals(marginCollaterals, currencies) {
  if (currencies.length === 0) {
    return marginCollaterals;
  }
  return marginCollaterals.map((o) => {
    const currencyInfo = currencies.find((item) => item.currency === o.currency);
    if (!currencyInfo) {
      console.warn('not found config for ' + o.currency + ' currency in currencies list');
      return o;
    }
    return {
      ...o,
      name: currencyInfo.name,
      displayCurrency: currencyInfo.displayCurrency,
      valuePrecision: currencyInfo.valuePrecision,
      pricePrecision: currencyInfo.pricePrecision,
      status: currencyInfo.status,
      valueFactor: 1,
      valueScale: 0,
    };
  });
}

/**
 * contracts 合约列表
 * contractSymbols 合约隐私币列表
 * categories 交易对所属分类
 * */

function convertContractData(contracts, contractSymbols, categories) {
  return contracts
    .filter(({symbol}) => !contractSymbols.includes(symbol))
    .map((contract) => {
      let categoryIds = [];
      categories.filter(({coins}) => coins.includes(contract.baseCurrency)).map(({id}) => categoryIds.push(id));
      return {...contract, categoryIds};
    });
}

/**
 * spots 现货列表
 * spotSymbols 现货隐私币列表
 * currencies 货币列表
 * categories 交易对所属分类
 * collateralTradeCurrencies 抵押品列表
 * */
function convertSpotsData(spots, spotSymbols, currencies, categories, collateralTradeCurrencies) {
  return spots
    .filter(({symbol}) => !spotSymbols.includes(symbol))
    .map((spot) => {
      const base = currencies.find(({currency}) => currency === spot.baseCurrency);
      const name = base ? base.name : '';
      /**
       * spot 资产是否可以进行 MarginTrading 前提条件：
       *    1: 不是子账户
       *    2: 资产已配置 leverage
       *    3: 资产的 baseCurrency 和 quoteCurrency 必须都在 collaterals 可交易列表中
       */
      const canMarginTrade = spot.leverage && collateralTradeCurrencies.includes(spot.baseCurrency) && collateralTradeCurrencies.includes(spot.quoteCurrency);
      let categoryIds = [];
      categories.filter(({coins}) => coins.includes(spot.baseCurrency)).map(({id}) => categoryIds.push(id));
      return {...spot, name, categoryIds, canMarginTrade};
    });
}

const ContractReserveRatio = 0.1;
function convertContractCollateral(collaterals) {
  // 交易品种：usdt 正向合约
  // margin ratio 档位：不同档位的限制按 prd 来
  // reserve ratio = 0.1
  /**
   * markDelete: boolean; // true means deleted, false means in the list
   * 资产不存在可借贷不可抵押情况
   * 资产下线后，不可借贷、不可交易、不可转入、可还款、可转出
   */
  return collaterals
    .filter((o) => o.perpCollateral || o.perpLoan)
    .map((o) => {
      const online = !o.markDelete;
      return {
        currency: o.currency,
        code: o.code,
        coefficient: o.coefficient,
        liquidityInDescendingOrder: o.liquidityInDescendingOrder,
        canRepay: true,
        canBorrow: online && o.perpLoan,
        isPerpCollateral: o.perpCollateral,
        isPerpLoan: o.perpLoan,
        debtLimit: o.debtLimit || 0,
        convertRate: o.convertRate || '0',
        reserveRatio: ContractReserveRatio,
      };
    });
}
